import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  Snackbar,
  Alert,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { useLocation, useNavigate } from "react-router-dom";

import Header from "../../components/Header";
import { authGet, authPatch, unwrapRows } from "../../lib/authApi";

function pickRows(resp) {
  if (!resp) return [];
  if (Array.isArray(resp)) return resp;
  if (Array.isArray(resp.rows)) return resp.rows;
  if (Array.isArray(resp.data)) return resp.data;
  return unwrapRows(resp) || [];
}

const STATUS_OPTIONS = [
  "created",
  "printed",
  "collected",
  "received",
  "in_process",
  "completed",
  "cancelled",
];

function statusColor(status) {
  const s = String(status || "").toLowerCase();
  if (s === "completed") return "success";
  if (s === "received" || s === "in_process") return "info";
  if (s === "printed" || s === "collected") return "warning";
  if (s === "cancelled") return "error";
  return "default";
}

function fmtDateTime(v) {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}

function StatusDialog({ open, onClose, orgId, row, onSaved }) {
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState("created");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!open) return;
    setStatus(row?.status || "created");
    setNotes(row?.notes || "");
    setBusy(false);
  }, [open, row]);

  const save = async () => {
    if (!orgId || !row?.id) return;

    setBusy(true);
    try {
      await authPatch(`/api/specimen-tracking/${encodeURIComponent(row.id)}/status`, {
        org_id: orgId,
        status,
        notes,
      });

      onSaved?.();
      onClose();
    } catch (e) {
      console.error("Failed to update status:", e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Update Tracking Status</DialogTitle>

      <DialogContent dividers>
        <Grid container spacing={2} sx={{ mt: 0.25 }}>
          <Grid item xs={12}>
            <TextField
              select
              fullWidth
              size="small"
              label="Status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
            >
              {STATUS_OPTIONS.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Notes"
              fullWidth
              size="small"
              multiline
              minRows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={save} disabled={busy}>
          {busy ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function Tracking() {
  const navigate = useNavigate();
  const { search } = useLocation();
  const qs = useMemo(() => new URLSearchParams(search), [search]);

  const [toast, setToast] = useState({
    open: false,
    msg: "",
    severity: "success",
  });

  const [orgs, setOrgs] = useState([]);
  const [orgId, setOrgId] = useState(qs.get("org") || "");

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);

  const syncToUrl = (nextOrg) => {
    const url = new URL(window.location.href);
    if (nextOrg) url.searchParams.set("org", nextOrg);
    else url.searchParams.delete("org");
    navigate(`${url.pathname}?${url.searchParams.toString()}`, { replace: true });
  };

  useEffect(() => {
    let cancelled = false;

    async function loadOrgs() {
      try {
        const j = await authGet("/api/organizations");
        const list = j?.organizations || j?.rows || pickRows(j) || [];

        if (cancelled) return;
        setOrgs(list);

        if (!orgId && list.length) {
          setOrgId(list[0].id);
        }
      } catch (e) {
        if (!cancelled) {
          setOrgs([]);
          setToast({
            open: true,
            msg: `Failed to load organizations: ${e.message}`,
            severity: "error",
          });
        }
      }
    }

    loadOrgs();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchRows = async () => {
    if (!orgId) {
      setRows([]);
      return;
    }

    setLoading(true);
    try {
      const q = new URLSearchParams({ org_id: orgId });

      if (statusFilter && statusFilter !== "all") {
        q.set("status", statusFilter);
      }

      if (searchText.trim()) {
        q.set("search", searchText.trim());
      }

      const j = await authGet(`/api/specimen-tracking?${q.toString()}`);
      const list = pickRows(j);

      setRows(
        list.map((r) => ({
          ...r,
          id: r.id,
        }))
      );
    } catch (e) {
      setRows([]);
      setToast({
        open: true,
        msg: `Failed to load tracking rows: ${e.message}`,
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!orgId) return;
    syncToUrl(orgId);
    fetchRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId, statusFilter]);

  const filtered = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((r) =>
      `${r.tracking_code || ""} ${r.patient_name || ""} ${r.patient_external_id || ""} ${r.status || ""}`
        .toLowerCase()
        .includes(q)
    );
  }, [rows, searchText]);

  const columns = [
    {
      field: "tracking_code",
      headerName: "Tracking Code",
      width: 220,
    },
    {
      field: "patient_name",
      headerName: "Patient",
      flex: 1,
      minWidth: 180,
      renderCell: ({ row }) => row.patient_name || "—",
    },
    {
      field: "patient_external_id",
      headerName: "Patient ID",
      width: 150,
      renderCell: ({ row }) => row.patient_external_id || "—",
    },
    {
      field: "status",
      headerName: "Status",
      width: 130,
      renderCell: ({ row }) => (
        <Chip
          label={String(row.status || "").toUpperCase()}
          color={statusColor(row.status)}
          variant="outlined"
          size="small"
        />
      ),
    },
    {
      field: "specimen_source",
      headerName: "Specimen Source",
      width: 160,
      renderCell: ({ row }) => row.specimen_source || "—",
    },
    {
      field: "sample_type",
      headerName: "Sample Type",
      width: 150,
      renderCell: ({ row }) => row.sample_type || "—",
    },
    {
      field: "created_at",
      headerName: "Created",
      width: 180,
      renderCell: ({ row }) => fmtDateTime(row.created_at),
    },
    {
      field: "updated_at",
      headerName: "Updated",
      width: 180,
      renderCell: ({ row }) => fmtDateTime(row.updated_at),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 280,
      sortable: false,
      filterable: false,
      renderCell: ({ row }) => (
        <Box display="flex" gap={1}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setEditingRow(row);
              setDialogOpen(true);
            }}
          >
            Update Status
          </Button>

          <Button
            size="small"
            variant="contained"
            onClick={() =>
              navigate(
                `/barcode-generator?org=${encodeURIComponent(orgId)}&tracking=${encodeURIComponent(row.id)}`
              )
            }
          >
            Reprint
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <Box p={3} sx={{ pb: 6 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Header
          title="Tracking"
          subtitle="Track barcode/specimen lifecycle from creation through completion"
        />

        <Box display="flex" gap={1} alignItems="center">
          <TextField
            select
            size="small"
            label="Organization"
            value={orgId || ""}
            onChange={(e) => setOrgId(e.target.value)}
            sx={{ minWidth: 220 }}
          >
            {orgs.map((o) => (
              <MenuItem key={o.id} value={o.id}>
                {o.name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            size="small"
            label="Status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="all">All</MenuItem>
            {STATUS_OPTIONS.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            size="small"
            label="Search"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            sx={{ minWidth: 260 }}
          />

          <Button variant="outlined" onClick={fetchRows}>
            Refresh
          </Button>
        </Box>
      </Box>

      <Card>
        <CardContent>
          <Box sx={{ height: 640, width: "100%" }}>
            <DataGrid
              rows={filtered}
              columns={columns}
              loading={loading}
              disableRowSelectionOnClick
              pageSizeOptions={[25, 50, 100]}
              initialState={{
                pagination: { paginationModel: { page: 0, pageSize: 25 } },
              }}
            />
          </Box>
        </CardContent>
      </Card>

      <StatusDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        orgId={orgId}
        row={editingRow}
        onSaved={() => {
          fetchRows();
          setToast({
            open: true,
            msg: "Tracking status updated.",
            severity: "success",
          });
        }}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
      >
        <Alert severity={toast.severity} variant="filled">
          {toast.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}